import React from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Card,
  CardContent,
  Chip,
  IconButton,
  Stack,
  Grid,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Edit as EditIcon,
  Share as ShareIcon,
  ContentCopy as CopyIcon,
  Phone as PhoneIcon,
  Emergency as EmergencyIcon,
  ListAlt as StepsIcon,
  ContactPhone as ContactIcon
} from '@mui/icons-material';
import { trackRunbookAction } from '../utils/analytics';
import { hapticFeedback, shareRunbook, copyToClipboard, makeEmergencyCall, isMobile } from '../utils/mobile';

const RunbookDetail = ({ runbook, onBack, onEdit, familyId }) => {
  // Helper function to safely render text
  const safeText = (value) => {
    try {
      if (value === null || value === undefined) return '';
      if (typeof value === 'string') return value;
      if (typeof value === 'number') return String(value);
      if (typeof value === 'boolean') return String(value);
      if (typeof value === 'object') {
        // If it's a step object, try to get the title or description
        if (value.title) return value.title;
        if (value.description) return value.description;
        // Otherwise, convert to string representation
        try {
          return JSON.stringify(value);
        } catch {
          return '[Object]';
        }
      }
      return String(value);
    } catch (error) {
      console.error('Error in safeText:', error, 'Value:', value);
      return '[Error]';
    }
  };

  // Helper function to safely get step content
  const getStepContent = (step) => {
    try {
      if (typeof step === 'string') return step;
      if (typeof step === 'object' && step !== null) {
        // If it's a step object, try to get the description or title
        if (step.description) return step.description;
        if (step.title) return step.title;
        // Fallback to JSON representation
        try {
          return JSON.stringify(step);
        } catch {
          return '[Step Object]';
        }
      }
      return String(step);
    } catch (error) {
      console.error('Error getting step content:', error, 'Step:', step);
      return '[Error]';
    }
  };

  const handleEdit = async () => {
    await hapticFeedback();
    trackRunbookAction('edit_start', runbook.id, runbook.title);
    onEdit(runbook);
  };

  const handleShare = async () => {
    await hapticFeedback();
    await shareRunbook(runbook);
    trackRunbookAction('share', runbook.id, runbook.title);
  };

  const handleCopySteps = async () => {
    await hapticFeedback();
    try {
      const stepsText = runbook.steps.map((step, index) => `${index + 1}. ${getStepContent(step)}`).join('\n');
      const success = await copyToClipboard(`${safeText(runbook.title)}\n\n${stepsText}`);
      if (success) {
        // You could show a toast notification here
        console.log('Steps copied to clipboard');
      }
    } catch (error) {
      console.error('Error copying steps:', error);
    }
  };

  const handleEmergencyCall = async (contact) => {
    await hapticFeedback();
    await makeEmergencyCall(contact.phone);
    trackRunbookAction('emergency_call', runbook.id, runbook.title);
  };

  return (
    <Container maxWidth="lg">
      <Box sx={{ py: 3 }}>
        {/* Header Section */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <IconButton onClick={onBack} sx={{ mr: 2 }}>
              <BackIcon />
            </IconButton>
            <Box sx={{ flex: 1 }}>
              <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
                <Chip 
                  label={safeText(runbook.category)} 
                  color="primary" 
                  variant="outlined"
                  icon={<StepsIcon />}
                />
                <Typography variant="h4" component="h1">
                  {safeText(runbook.title)}
                </Typography>
              </Stack>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                {safeText(runbook.description)}
              </Typography>
            </Box>
          </Box>

          {/* Action Buttons */}
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              startIcon={<EditIcon />}
              onClick={handleEdit}
              size="large"
            >
              Edit Runbook
            </Button>
            
            {isMobile() && (
              <>
                <Button
                  variant="outlined"
                  startIcon={<ShareIcon />}
                  onClick={handleShare}
                  size="large"
                >
                  Share
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<CopyIcon />}
                  onClick={handleCopySteps}
                  size="large"
                >
                  Copy Steps
                </Button>
              </>
            )}
          </Box>
        </Box>

        <Grid container spacing={4}>
          {/* Steps Section */}
          <Grid item xs={12} lg={8}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                  <StepsIcon sx={{ mr: 1, color: 'primary.main' }} />
                  <Typography variant="h5" component="h2">
                    Steps to Follow
                  </Typography>
                </Box>
                
                <List>
                  {runbook.steps && Array.isArray(runbook.steps) && runbook.steps.map((step, index) => (
                    <ListItem key={index} sx={{ px: 0, py: 2 }}>
                      <ListItemIcon sx={{ minWidth: 40 }}>
                        <Paper
                          sx={{
                            width: 32,
                            height: 32,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: 'primary.main',
                            color: 'white',
                            fontWeight: 'bold',
                            fontSize: '0.875rem'
                          }}
                        >
                          {index + 1}
                        </Paper>
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Box>
                            {typeof step === 'object' && step !== null ? (
                              <Box>
                                {step.title && (
                                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                                    {step.title}
                                  </Typography>
                                )}
                                {step.description && (
                                  <Typography variant="body2" sx={{ mb: 1 }}>
                                    {step.description}
                                  </Typography>
                                )}
                                {(step.estimated_time || step.notes) && (
                                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                    {step.estimated_time && (
                                      <Chip 
                                        size="small" 
                                        label={`Time: ${step.estimated_time}`} 
                                        color="primary" 
                                        variant="outlined"
                                      />
                                    )}
                                    {step.notes && (
                                      <Chip 
                                        size="small" 
                                        label={`Notes: ${step.notes}`} 
                                        color="secondary" 
                                        variant="outlined"
                                      />
                                    )}
                                  </Box>
                                )}
                              </Box>
                            ) : (
                              <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
                                {getStepContent(step)}
                              </Typography>
                            )}
                          </Box>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              </CardContent>
            </Card>
          </Grid>

          {/* Emergency Contacts Section */}
          {runbook.emergency_contacts && Array.isArray(runbook.emergency_contacts) && runbook.emergency_contacts.length > 0 && (
            <Grid item xs={12} lg={4}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <ContactIcon sx={{ mr: 1, color: 'error.main' }} />
                    <Typography variant="h5" component="h2">
                      Emergency Contacts
                    </Typography>
                  </Box>
                  
                  <Stack spacing={2}>
                    {runbook.emergency_contacts.map((contact, index) => (
                      <Paper key={index} variant="outlined" sx={{ p: 2 }}>
                        <Typography variant="h6" component="h3" gutterBottom>
                          {safeText(contact.name)}
                        </Typography>
                        
                        {isMobile() ? (
                          <Button
                            variant="contained"
                            color="error"
                            startIcon={<EmergencyIcon />}
                            fullWidth
                            onClick={() => handleEmergencyCall(contact)}
                            sx={{ mt: 1 }}
                          >
                            Call {safeText(contact.phone)}
                          </Button>
                        ) : (
                          <Button
                            variant="outlined"
                            startIcon={<PhoneIcon />}
                            fullWidth
                            href={`tel:${safeText(contact.phone)}`}
                            sx={{ mt: 1 }}
                          >
                            {safeText(contact.phone)}
                          </Button>
                        )}
                      </Paper>
                    ))}
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          )}
        </Grid>
      </Box>
    </Container>
  );
};

export default RunbookDetail;